/**
 * Configurações do usuário (taxa, recebimento, absorção).
 * Persiste em localStorage (pcp_settings).
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

const STORAGE_KEY = 'pcp_settings';

const DEFAULT_SETTINGS = {
  /** Ao receber pagamento no Receber: true = oferece "Pagar Taxa" / "Pagar depois"; false = só registra como pendente */
  askFeePaymentOnReceive: true,
  /** true = quem gera o QR (cobrador) absorve e paga a taxa de transferência (VITE_FEE_PERCENT); false = quem envia paga. A taxa de recebimento (VITE_FEE_BILLING_PERCENT) é sempre paga pelo cobrador. */
  absorbTransferFee: false,
  /** Ao trocar a Moeda de Exibição no campo Valor: true = converter pela cotação atual; false = manter o mesmo número */
  convertAmountOnCurrencyChange: true,
  /** Idioma da interface: 'pt-BR' | 'en' (sincronizado com i18n e pcp_locale) */
  locale: 'pt-BR',
  /** Moeda de exibição na tela de transferências: 'USD' | 'BRL' */
  displayCurrency: 'USD',
  /** Quando true, moeda de exibição não pode ser alterada nas telas Enviar/Receber (selector oculto). Revertível: remover esta chave e usos de lockDisplayCurrency. */
  lockDisplayCurrency: false,
  /** Opções do QR "Compartilhar carteira" (Configurações): o que incluir no JSON */
  includeNameInWalletQR: true,
  includeEmailInWalletQR: true,
  includePhoneInWalletQR: false,
  includeNetworkInWalletQR: true,
  includeTagsInWalletQR: false,
  includePhotoInWalletQR: false,
  /** Sistema de notificações via Firestore (flags temporárias, visíveis apenas para admins na UI) */
  enableFirestoreNotifications: true,
  enableOnChainVerificationForNotifications: true,
  /** Quando true, desliga o polling legado de recebimento e depende apenas do Firestore. */
  disableLegacyIncomingPolling: true,
  /** Quando true, cria notificação em transferNotifications após envio bem-sucedido. */
  enableCreateNotificationOnSend: true,
};

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useLocalStorage(STORAGE_KEY, DEFAULT_SETTINGS);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const value = useMemo(
    () => {
      const merged = { ...DEFAULT_SETTINGS, ...settings };
      // Retrocompat: settings antigos usavam billingAbsorbPercent (0–100); 100% = absorver = true
      if (merged.absorbTransferFee === undefined && merged.billingAbsorbPercent !== undefined) {
        merged.absorbTransferFee = merged.billingAbsorbPercent === 100;
      }
      return { ...merged, updateSetting, setSettings };
    },
    [settings],
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  return (
    ctx || {
      ...DEFAULT_SETTINGS,
      updateSetting: () => {},
      setSettings: () => {},
    }
  );
}

export { DEFAULT_SETTINGS };
